import React, { useState, useEffect } from "react";
import "../QuoteForm.css";

const parseRow = (row) => {
  const header = (row.sectionHeader || "").trim();
  if (header) {
    return { section: header, subcategory: (row.category || "").trim() };
  }
  const lines = (row.category || "").split("\n");
  return {
    section: (lines[0] || "").trim(),
    subcategory: lines.slice(1).join("\n").trim(),
  };
};

const PACKAGE_NAMES = ["아마존 패키지", "아마존 킨들 출간"];
const isPackageRow = (category) =>
  (category || "").split("\n").some((line) => PACKAGE_NAMES.includes(line.trim()));

const sortServiceOptions = (services) => {
  if (!services) return [];

  const order = (v) => (v == null ? Number.MAX_SAFE_INTEGER : v);

  const sectionMap = new Map();
  services.forEach((s) => {
    const { section, subcategory } = parseRow(s);
    if (!sectionMap.has(section)) sectionMap.set(section, new Map());
    const subMap = sectionMap.get(section);
    if (!subMap.has(subcategory)) subMap.set(subcategory, []);
    subMap.get(subcategory).push(s);
  });

  const sections = [];
  sectionMap.forEach((subMap, section) => {
    const subgroups = [];
    subMap.forEach((items, subcategory) => {
      items.sort((a, b) => order(a.displayOrder) - order(b.displayOrder));
      const minOrder = Math.min(...items.map((i) => order(i.displayOrder)));
      subgroups.push({ subcategory, items, minOrder });
    });
    subgroups.sort((a, b) => a.minOrder - b.minOrder);
    sections.push({
      section,
      subgroups,
      minOrder: Math.min(...subgroups.map((g) => g.minOrder)),
    });
  });
  sections.sort((a, b) => a.minOrder - b.minOrder);
  return sections;
};

function ServiceOptionSection({
  serviceOptions,
  handleCheckboxChange,
  handleQuantityChange,
  renderExtraComponent,
}) {
  const [sortedSections, setSortedSections] = useState([]);

  const pageCountAffectedServiceTitles = [
    "전문 편집",
    "전문 감수",
    "본문 내지 디자인",
    "번역 서비스",
  ];
  const quantityInputExclusions = ["맞춤형 표지 디자인"];

  // Amazon-specific row behaviors (title/price/category driven, no hardcoded IDs):
  //  - negotiable: unitPrice 0 renders "비용 협의" and is excluded from totals
  //  - locked qty: 하드커버 출간 and package rows (Basic / Pro) are always quantity 1
  const isNegotiable = (item) => Number(item.unitPrice) === 0;
  const isQtyLocked = (item) =>
    item.title === "하드커버 출간" || isPackageRow(item.category);

  const calculateItemTotal = (item) => {
    return item.isSelected ? item.qty * item.unitPrice : 0;
  };

  useEffect(() => {
    if (serviceOptions) {
      setSortedSections(sortServiceOptions(serviceOptions));
    }
  }, [serviceOptions]);

  const renderRow = (item) => (
    <React.Fragment key={item.serviceOptionId}>
      <div className="qt-row-v2">
        <div className="qt-cell-v2 check-cell">
          <input
            type="checkbox"
            checked={item.isSelected}
            onChange={() => handleCheckboxChange(item.serviceOptionId)}
            disabled={item.required}
          />
        </div>
        <div
          className="qt-cell-v2 item-name"
          style={{ whiteSpace: "pre-line", textAlign: "left" }}
        >
          {item.title}
        </div>
        <div
          className="qt-cell-v2 description-cell"
          style={{ whiteSpace: "pre-line" }}
        >
          {item.description}
        </div>
        <div className="qt-cell-v2 qty-group">
          {!quantityInputExclusions.includes(item.title) && !isNegotiable(item) && (
            <>
              <input
                type="number"
                value={isQtyLocked(item) ? 1 : item.qty}
                className="qty-input"
                onChange={(e) =>
                  handleQuantityChange(item.serviceOptionId, e.target.value)
                }
                disabled={
                  item.required ||
                  isQtyLocked(item) ||
                  pageCountAffectedServiceTitles.includes(item.title)
                }
                min="0"
              />
              <span className="unit-label">{item.unit || ""}</span>
            </>
          )}
        </div>
        <div className="qt-cell-v2 text-right">
          {isNegotiable(item) ? "비용 협의" : item.unitPrice.toLocaleString()}
        </div>
        <div className="qt-cell-v2 text-right">
          {isNegotiable(item)
            ? "비용 협의"
            : calculateItemTotal(item).toLocaleString()}
        </div>
      </div>
      {item.extraInfo && <div className="qt-extra-info">{item.extraInfo}</div>}
    </React.Fragment>
  );

  return (
    <div className="services-and-quote-section">
      <h2>서비스 항목 및 견적</h2>
      <div className="quote-table-header new-header">
        <div>선택</div>
        <div>서비스 항목</div>
        <div>서비스 내용</div>
        <div>수량</div>
        <div>단가(원)</div>
        <div>금액(원)</div>
      </div>
      {sortedSections.map((sec, sectionIndex) => (
        <React.Fragment key={sec.section}>
          <div className="service-category-block">
            <div className="qt-category-header">{sec.section}</div>
            {sec.subgroups.map((group) => (
              <React.Fragment key={group.subcategory || "_default"}>
                {group.subcategory && (
                  <div className="qt-category-subheader">{group.subcategory}</div>
                )}
                <div className="service-items-container">
                  {group.items.map((item) => renderRow(item))}
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Optional component injected after a section (inside this wrapper so it
              shares the section styling). Receives the section index and the full
              section object so callers can anchor by name or by contained category. */}
          {renderExtraComponent?.(sectionIndex, sec)}
        </React.Fragment>
      ))}
    </div>
  );
}

export default ServiceOptionSection;
export { isPackageRow };
